import { motion } from "framer-motion";
import { HeartPulse, UserCheck, XCircle, CheckCircle2, Activity, ArrowRight, Workflow, FlaskConical } from "lucide-react";
import { useHive } from "@/store/useHive";
import { useExecution } from "@/store/executionStore";
import { useSampleDataCta } from "@/hooks/useSampleDataCta";
import { SampleBadge } from "@/components/ui/SampleBadge";

const accent = "#22d3ee";

/** Compact desktop surface — the live heartbeat of Hive. */
export function ExecutionCenterWidget() {
  const openApp = useHive((s) => s.openApp);
  const executions = useExecution((s) => s.executions);
  const approvals = useExecution((s) => s.approvals);
  const health = useExecution((s) => s.systemHealth);
  const selectExecution = useExecution((s) => s.selectExecution);
  const setCenterView = useExecution((s) => s.setCenterView);
  const sampleCta = useSampleDataCta();

  const running = executions.filter((e) => e.status === "running").length;
  const pending = approvals.filter((a) => a.status === "pending").length;
  const failed = executions.filter((e) => e.status === "failed").length;
  const completed = executions.filter((e) => e.status === "completed").length;
  const unhealthy = health.filter((h) => h.status !== "healthy").length;
  const topRun = executions.find((e) => e.status === "running");

  const stop = (e: React.PointerEvent) => e.stopPropagation();

  const cells = [
    { n: pending, l: "approval", color: "#a855f7", icon: UserCheck },
    { n: failed, l: "failed", color: "#f43f5e", icon: XCircle },
    { n: completed, l: "done", color: "#22c55e", icon: CheckCircle2 },
  ];

  // Fresh accounts: nothing has run yet.
  if (executions.length === 0) {
    return (
      <div className="w-[330px] cursor-pointer" onClick={() => openApp("execution")}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-medium text-app-2">
            <HeartPulse size={14} style={{ color: accent }} /> Execution Center
          </div>
          <span className="flex items-center gap-1 text-[11px] text-app-2">
            <Activity size={11} /> idle
          </span>
        </div>
        <div className="rounded-xl border border-dashed border-app bg-surface-2/40 p-4 text-center">
          <div className="text-sm font-semibold text-app">Nothing has run yet</div>
          <div className="mt-1 text-xs text-app-2">
            Every workflow run lands here with live steps, logs and approvals.
          </div>
          <button
            onPointerDownCapture={stop}
            onClick={(e) => { e.stopPropagation(); openApp("workflows"); }}
            className="mt-3 inline-flex items-center justify-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-semibold text-black transition hover:brightness-110"
            style={{ background: accent }}
          >
            <Workflow size={12} /> Build a workflow
          </button>
          {sampleCta && (
            <button
              onPointerDownCapture={stop}
              onClick={(e) => { e.stopPropagation(); sampleCta.run(); }}
              className="mt-1.5 inline-flex items-center justify-center gap-1 rounded-lg border border-app px-3 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
            >
              <FlaskConical size={12} /> {sampleCta.label}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[330px] cursor-pointer" onClick={() => openApp("execution")}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-app-2">
          <HeartPulse size={14} style={{ color: accent }} /> Execution Center
        </div>
        <span className="flex items-center gap-1 text-[11px]" style={{ color: unhealthy ? "#f59e0b" : "#22c55e" }}>
          <Activity size={11} /> {unhealthy ? `${unhealthy} degraded` : "all healthy"}
        </span>
      </div>

      <div className="flex items-end gap-3">
        <div>
          <div className="flex items-end gap-1.5">
            <span className="text-4xl font-semibold leading-none text-app tabular-nums">{running}</span>
            <span className="relative mb-1 flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{ background: accent }} />
              <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: accent }} />
            </span>
          </div>
          <div className="mt-1 text-[11px] text-app-2">running now</div>
        </div>
        <div className="mb-0.5 grid flex-1 grid-cols-3 gap-1.5">
          {cells.map((c) => (
            <div key={c.l} className="rounded-lg border border-app bg-surface-2/40 py-1.5 text-center">
              <div className="flex items-center justify-center gap-1 text-sm font-semibold tabular-nums" style={{ color: c.color }}>
                <c.icon size={11} /> {c.n}
              </div>
              <div className="text-[9px] uppercase tracking-wide text-app-2">{c.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* top active run */}
      {topRun && (
        <div className="mt-3 rounded-xl border border-app bg-surface-2/40 px-3 py-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: accent }}>Active</span>
            <span className="truncate text-[11px] font-medium text-app">{topRun.name}</span>
            {topRun.isSample && <SampleBadge />}
          </div>
          <motion.div className="mt-0.5 truncate text-[10px] text-app-2" animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2.4, repeat: Infinity }}>
            ▸ {topRun.currentStep} · {topRun.agent ?? "agent"}
          </motion.div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-1.5">
        <button
          onPointerDownCapture={stop}
          onClick={(e) => { e.stopPropagation(); setCenterView("executions"); if (topRun) selectExecution(topRun.id); openApp("execution"); }}
          className="flex items-center justify-center gap-1 rounded-lg py-1.5 text-[11px] font-semibold text-black transition hover:brightness-110"
          style={{ background: accent }}
        >
          <Activity size={12} /> Open Center
        </button>
        <button
          onPointerDownCapture={stop}
          onClick={(e) => { e.stopPropagation(); setCenterView("approvals"); openApp("execution"); }}
          className="flex items-center justify-center gap-1 rounded-lg border border-app bg-surface-2/60 py-1.5 text-[11px] font-medium text-app-1 transition hover:border-app-strong"
        >
          <UserCheck size={12} /> Approvals {pending > 0 && <span className="text-fuchsia-400">· {pending}</span>} <ArrowRight size={11} />
        </button>
      </div>
    </div>
  );
}
